'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { Discount } from '@/lib/Apis/models/discount';
import { CartItemPopulated } from '@/lib/Apis/models';
import { DiscountsApi } from '@/lib/Apis/apis/discounts-api';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { calculateDiscountAmount } from '@/lib/utils/discount';

interface DiscountContextType {
  appliedCoupon: Discount | null;
  couponError: string | null;
  isValidatingCoupon: boolean;
  validateAndApplyCoupon: (code: string, userId?: string, cartItems?: CartItemPopulated[]) => Promise<{ success: boolean; error?: string; discount?: Discount }>;
  removeCoupon: () => void;
  calculateCouponDiscount: (subtotal: number) => number;
}

const DiscountContext = createContext<DiscountContextType | undefined>(undefined);

export function DiscountProvider({ children }: { children: React.ReactNode }) {
  const [appliedCoupon, setAppliedCoupon] = useState<Discount | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const validateAndApplyCoupon = useCallback(
    async (code: string, userId?: string, cartItems?: CartItemPopulated[]) => {
      setIsValidatingCoupon(true);
      setCouponError(null);

      try {
        const api = new DiscountsApi(AXIOS_CONFIG);
        const bodyOptions = cartItems && cartItems.length > 0 ? { data: { products: cartItems } } : {};
        const response = await api.validateCoupon(code, userId, undefined, bodyOptions);
        const discount = response.data;

        if (!discount) {
          setCouponError('Coupon code not found');
          setAppliedCoupon(null);
          return { success: false, error: 'Coupon code not found' };
        }

        setAppliedCoupon(discount);
        setCouponError(null);
        return { success: true, discount };
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || 'Failed to validate coupon code';
        setCouponError(errorMessage);
        setAppliedCoupon(null);
        return { success: false, error: errorMessage };
      } finally {
        setIsValidatingCoupon(false);
      }
    },
    []
  );

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponError(null);
  }, []);

  const calculateCouponDiscount = useCallback(
    (subtotal: number): number => {
      if (!appliedCoupon) {
        return 0;
      }
      const discount = calculateDiscountAmount(subtotal, appliedCoupon);
      return discount;
    },
    [appliedCoupon]
  );

  const value: DiscountContextType = {
    appliedCoupon,
    couponError,
    isValidatingCoupon,
    validateAndApplyCoupon,
    removeCoupon,
    calculateCouponDiscount,
  };

  return (
    <DiscountContext.Provider value={value}>
      {children}
    </DiscountContext.Provider>
  );
}

export function useDiscountsContext() {
  const context = useContext(DiscountContext);
  if (!context) {
    throw new Error('useDiscountsContext must be used within DiscountProvider');
  }
  return context;
}

export type { DiscountContextType };
